import Joi from "joi-browser";
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { json2xml } from "xml-js";

import contactOptionSets from "../../options/contact.json";
import {
  checkKeyPress,
  createEntity,
  getFieldDate,
  getFieldText,
} from "../../scripts/helperScripts";
import {
  getReferringAgencies,
  saveReferral,
  searchContact,
  writeErrorLog,
} from "../services/soar";
import Input from "./Input";
import SearchBox from "./SearchBox";
import Select from "./Select";

function ReferralModal({
  isOpen,
  hideModal,
  programUnitOptions,
  updateReferrals,
}) {
  const [data, setData] = useState({ addNewClient: false });
  const [referringAgencySearchQuery, setReferringAgencySearchQuery] =
    useState("");
  const [referringAgencyOptions, setReferringAgencyOptions] = useState([]);
  const [agenciesLoading, setAgenciesLoading] = useState(false);
  const [workerSearchQuery, setWorkerSearchQuery] = useState("");
  const [workerOptions, setWorkerOptions] = useState([]);
  const [workerSearchCompleted, setWorkerSearchCompleted] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearchCompleted, setClientSearchCompleted] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  var org =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "ReactLive"
      : "ReactDev";
  var logOrg =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "SOARLive"
      : "SOARD365Base";

  const joiSchema = {
    client: Joi.string()
      .label("Client")
      .when("addNewClient", { is: false, then: Joi.string().required() }),
    dateOfReferral: Joi.date()
      .label("Date of Referral")
      .required()
      .max("now")
      .min(new Date("1/1/1900"))
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date of Referral cannot be future dated`;
              break;
            case "date.min":
              error.message = `Date of Referral cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    referringAgency: Joi.string().allow(""),
    referringWorker: Joi.string().allow(""),
    firstName: Joi.string().label("First Name").when("addNewClient", {
      is: true,
      then: Joi.string().required(),
    }),
    middleName: Joi.string().label("Middle Name"),
    lastName: Joi.string().label("Last Name").when("addNewClient", {
      is: true,
      then: Joi.string().required(),
    }),
    dateOfBirth: Joi.date()
      .label("Date of Birth")
      .max("now")
      .min(new Date("1/1/1900"))
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date of Birth cannot be future dated`;
              break;
            case "date.min":
              error.message = `Date of Birth cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      })
      .allow(""),
    sex: Joi.string().label("Sex").allow(""),
    ssn: Joi.string().label("SSN").max(11).allow(""),
    programUnit: Joi.string().required().label("Program Unit"),
    addNewClient: Joi.boolean(),
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const businessUnitIdName = useMemo(() => {
    if (!safyInfo?.user?.safyInfo?.BusinessUnitIdName) {
      return;
    }

    return safyInfo.user.safyInfo.BusinessUnitIdName;
  }, [safyInfo]);

  useEffect(() => {
    const loadReferringAgencies = async () => {
      setAgenciesLoading(true);
      const response = await getReferringAgencies(businessUnitIdName);

      if (!isMountedRef.current) {
        return false;
      }
      if (response) {
        const options = response.map((referringAgency) => ({
          _id: referringAgency.accountid,
          name: referringAgency.name,
        }));

        setReferringAgencyOptions(options);
      }

      setAgenciesLoading(false);
    };

    loadReferringAgencies();
  }, [businessUnitIdName]);

  const handleClientSearch = async () => {
    setClientSearchCompleted(false);
    const response = await searchContact(
      clientSearchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );
    if (!isMountedRef.current) {
      return;
    }

    if (response) {
      const options = response.map((contact) => {
        return {
          _id: contact.contactid,
          name: `${contact.fullname}${
            contact.birthdate
              ? " - DOB: " + getFieldDate(contact.birthdate)
              : ""
          }`,
          firstName: contact.firstname,
          middleName: contact.middlename,
          lastName: contact.lastname,
          genderCode: contact.gendercode
            ? getFieldText(contact.gendercode)
            : null,
          ssn: contact.ccx_ssn,
          dateOfBirth: contact.birthdate
            ? getFieldDate(contact.birthdate)
            : null,
        };
      });
      setClientOptions(options);
    }

    setClientSearchCompleted(true);
  };

  const handleWorkerSearch = async () => {
    setWorkerSearchCompleted(false);
    const response = await searchContact(
      workerSearchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );

    if (!isMountedRef.current) {
      return;
    }

    if (response) {
      const options = response.map((contact) => {
        return {
          _id: contact.contactid,
          name: `${contact.fullname}${
            contact.birthdate
              ? " - DOB: " + getFieldDate(contact.birthdate)
              : ""
          }`,
        };
      });
      setWorkerOptions(options);
    }

    setWorkerSearchCompleted(true);
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);

    setErrors(errs);
  };

  const handleAddNewClientChange = (isNewClient) => {
    const obj = { ...data };
    obj.addNewClient = isNewClient;
    setData(obj);

    const errs = { ...errors };
    delete errs.firstName;
    delete errs.lastName;
    delete errs.sex;
    delete errs.dateOfBirth;
    delete errs.client;
    setErrors(errs);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = (isSaveAndAdd = false) => {
    let referral = {};

    const demographics = {
      // demo: {
      //   type: "section",
      //   label: "Additional Demographics",
      // },
      // preferredGender: {
      //   type: "select",
      //   label: "Preferred Gender",
      //   text: "",
      //   value: "",
      //   options: contactOptionSets.sor_preferredgender,
      // },
      // consideryourselfhispaniclatino: {
      //   type: "checkbox",
      //   label: "Considered Hispanic or Latino",
      //   value: false,
      //   text: "false",
      // },
      // race: {
      //   type: "section",
      //   label: "Race",
      // },
      // americanindianoralaskanative: {
      //   type: "checkbox",
      //   label: "American Indian or Alaskan Native",
      //   value: false,
      //   text: "false",
      // },
      // nativehawaiianorotherpacificislander: {
      //   type: "checkbox",
      //   label: "Native Hawaiian or Pacific Islander",
      //   value: false,
      //   text: "false",
      // },
      // blackorafricanamerican: {
      //   type: "checkbox",
      //   label: "Black or African American",
      //   value: false,
      //   text: "false",
      // },
      // white: {
      //   type: "checkbox",
      //   label: "White",
      //   value: false,
      //   text: "false",
      // },
      // asian: {
      //   type: "checkbox",
      //   label: "Asian",
      //   value: false,
      //   text: "false",
      // },
      // primarylanguage: {
      //   type: "select",
      //   label: "Primary Language",
      //   text: "",
      //   value: "",
      //   options: contactOptionSets.sor_primarylanguage,
      // },
    };

    const selectedProgramUnit = programUnitOptions.filter(
      (p) => p._id === data.programUnit
    )[0];
    const referralConfig = selectedProgramUnit.referralConfig;

    let referralObj = { ...demographics, ...referralConfig };

    if (referralObj.deferralReasons === undefined) {
      referralObj.deferralReasons = {
        type: "deferralReasons",
        text: "",
        value: "",
      };
    }

    if (data.referringWorker !== undefined && data.referringWorker !== "") {
      const worker = workerOptions.filter(
        (w) => w._id === data.referringWorker
      )[0];
      referralObj.referringWorker = {
        type: "referringWorker",
        text: worker.name,
        value: data.referringWorker,
      };
    }

    referral.ReferralId = "";
    referral.ProposedProgram = data.programUnit;
    referral.ProposedProgramName = programUnitOptions.filter(
      (p) => p._id === data.programUnit
    )[0].name;
    referral.SOARClientProgramId = "";
    referral.SOARClientProgramName = "";
    referral.DynamicFields =
      "<results>" +
      json2xml(referralObj, { compact: true, igonoreComment: true }) +
      "</results>";
    referral.Status = "Pending";
    referral.StatusChangeDate = new Date();
    referral.CreatedById = safyInfo.user.azureId;
    referral.ModifiedById = safyInfo.user.azureId;
    referral.DeferralReason = "";
    referral.DateOfReferral = new Date(data.dateOfReferral);
    referral.ReferringAgencyId = "";
    referral.ReferringAgencyName = "";

    if (data.referringAgency !== undefined && data.referringAgency !== "") {
      const agency = referringAgencyOptions.filter(
        (item) => item._id === data.referringAgency
      )[0];

      referral.ReferringAgencyId = agency._id;
      referral.ReferringAgencyName = agency.name;
    }

    if (data.addNewClient) {
      const contact = {
        ccx_contacttype: {
          type: "optionset",
          value: 803080001,
        },
        firstname: {
          type: "string",
          value: data.firstName,
        },
        lastname: {
          type: "string",
          value: data.lastName,
        },
      };

      if (data.middleName !== undefined && data.middleName !== "")
        contact.middlename = {
          type: "string",
          value: data.middleName,
        };

      if (data.dateOfBirth !== undefined && data.dateOfBirth !== "")
        contact.birthdate = {
          type: "datetime",
          value: new Date(data.dateOfBirth),
        };

      if (data.ssn && data.ssn !== "")
        contact.ccx_ssn = {
          type: "string",
          value: data.ssn,
        };

      if (data.sex && data.sex !== "")
        contact.gendercode = {
          type: "optionset",
          value: data.sex,
        };

      let toastId = toast.loading("Creating Contact...", {
        type: toast.TYPE.INFO,
      });

      createEntity(
        "contact",
        JSON.stringify(contact),
        safyInfo,
        selectedProgramUnit.state
      )
        .then((response) => {
          toast.update(toastId, {
            render: "Contact has been created.",
            type: toast.TYPE.SUCCESS,
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
          });

          const selectedGender = contactOptionSets.gendercode.filter(
            (code) => code._id === data.sex
          )[0];

          referral = {
            ...referral,
            FirstName: data.firstName,
            MiddleName: data.middleName || "",
            LastName: data.lastName,
            DOB: data.dateOfBirth ? new Date(data.dateOfBirth) : "",
            GenderValue: data.sex || "",
            GenderText: data.sex ? selectedGender.name : "",
            SSN: data.ssn || "",
            SOARContactId: response.resultset.result.contactid,
          };

          toastId = toast.loading("Creating Referral...", {
            type: toast.TYPE.INFO,
          });
          saveReferral(referral, org, "Insert").then((response) => {
            if (response) {
              const result = JSON.parse(response);
              if (
                result.SystemResponse &&
                result.SystemResponse[0] &&
                result.SystemResponse[0].Code === "SUCCESS"
              ) {
                updateReferrals(true);
                toast.update(toastId, {
                  render: "Referral has been created.",
                  type: toast.TYPE.SUCCESS,
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
                if (!isSaveAndAdd)
                  navigate(`/referral/${result.SystemResponse[0].Id}`);
              } else {
                toast.update(toastId, {
                  render: "An error occurred while creating the Referral.",
                  type: toast.TYPE.ERROR,
                  isLoading: false,
                  autoClose: 5000,
                  closeOnClick: true,
                });
                writeLog(referral, "Insert", response);
              }
            } else {
              toast.update(toastId, {
                render: "An error occurred while creating the Referral.",
                type: toast.TYPE.ERROR,
                isLoading: false,
                autoClose: 5000,
                closeOnClick: true,
              });
              writeLog(referral, "Insert", response);
            }
          });
        })
        .catch((error) => {
          toast.update(toastId, {
            render: "An error occurred while creating the Contact.",
            type: toast.TYPE.ERROR,
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
          });
          writeLog(contact, "Insert", error);
        });
    } else {
      const selectedClient = clientOptions.filter(
        (option) => option._id === data.client
      )[0];
      const clientGender = contactOptionSets.gendercode.filter(
        (code) => code._id === selectedClient.genderCode
      )[0];
      referral = {
        ...referral,
        FirstName: selectedClient.firstName,
        MiddleName: selectedClient.middleName || "",
        LastName: selectedClient.lastName,
        DOB: selectedClient.dateOfBirth || "",
        GenderValue: selectedClient.genderCode || "",
        GenderText: (clientGender && clientGender.name) || "",
        SSN: selectedClient.ssn || "",
        SOARContactId: data.client,
      };

      saveReferral(referral, org, "Insert")
        .then((response) => {
          if (response) {
            const result = JSON.parse(response);
            if (
              result.SystemResponse &&
              result.SystemResponse[0] &&
              result.SystemResponse[0].Code === "SUCCESS"
            ) {
              updateReferrals(true);
              toast.success("Referral Successfully Created");
              if (!isSaveAndAdd)
                navigate(`/referral/${result.SystemResponse[0].Id}`);
            } else {
              toast.error("A problem has occurred when creating the Referral");
              writeLog(referral, "Insert", response);
            }
          } else {
            toast.error("A problem has occurred when creating the Referral");
            writeLog(referral, "Insert", response);
          }
        })
        .catch((error) => {
          toast.error("A problem has occurred when creating the Referral");
          writeLog(referral, "Insert", error);
        });
    }
  };

  const writeLog = (bodyObj, Action, response) => {
    const logData = {
      Who: `${safyInfo.user.domainName}|${safyInfo.user.displayName}`,
      API: "Eureka API",
      APIBody: JSON.stringify(bodyObj),
      URL: `https://eurekaapi.azurewebsites.net/Component/Upsert/Referral/${org}/${Action}/`,
      Org: logOrg,
      Application: "Referral Modal - Insert",
      FullError: JSON.stringify({
        response,
      }),
      ResponseCode: response.Status || "Unknown",
      ResponseMessage: response.Message || "Unknown",
      ExceptionType: response.ClassName || "Unknown",
    };
    writeErrorLog(logData);
  };

  const handleSaveAndAdd = () => {
    handleSave(true);
    resetForm();
  };

  const resetForm = () => {
    setData({ addNewClient: false });
    setClientOptions([]);
    setClientSearchQuery("");
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const getReferringAgencyOptions = () => {
    const options = referringAgencyOptions.filter(
      (option) =>
        referringAgencySearchQuery === "" ||
        option.name
          .toLowerCase()
          .includes(referringAgencySearchQuery.toLowerCase())
    );

    return options;
  };

  const handkeKeyDown = (e) => {
    if (checkKeyPress(e)) {
      handleClientSearch();
    }
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false} onHide={() => void 0}>
          <Modal.Header>
            <Modal.Title>Referral</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col">
                <SearchBox
                  name="clientSearchQuery"
                  label="Search for Client"
                  value={data.clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.currentTarget.value)}
                  clearSearch={() => setClientSearchQuery("")}
                  placeholder="Search for Client..."
                  disabled={data.addNewClient}
                  onKeyDown={handkeKeyDown}
                  borderClassName="border"
                  style={{ borderColor: "#dee2e6" }}
                />
              </div>
              <div className="col-3">
                <button
                  className="btn btn-primary"
                  style={{ marginTop: "30px" }}
                  onClick={handleClientSearch}
                  disabled={clientSearchQuery === ""}
                >
                  Search
                </button>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <Select
                  name="client"
                  label="Clients"
                  options={clientOptions}
                  onChange={handleChange}
                  error={errors.client}
                  disabled={!clientSearchCompleted || data.addNewClient}
                />
              </div>
              <div className="col-3">
                {!data.addNewClient && (
                  <button
                    className={
                      data.client ? "btn btn-secondary" : "btn btn-primary"
                    }
                    style={{ marginTop: "30px" }}
                    onClick={() => handleAddNewClientChange(true)}
                    disabled={!clientSearchCompleted || data.client}
                  >
                    New
                  </button>
                )}
                {data.addNewClient && (
                  <button
                    className="btn btn-danger"
                    style={{ marginTop: "30px" }}
                    onClick={() => handleAddNewClientChange(false)}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
            {data.addNewClient && (
              <>
                <div className="row">
                  <div className="col">
                    <Input
                      name="firstName"
                      label="First Name"
                      onChange={handleChange}
                      error={errors.firstName}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="lastName"
                      label="Last Name"
                      onChange={handleChange}
                      error={errors.lastName}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="dateOfBirth"
                      label="Date of Birth"
                      type="date"
                      onChange={handleChange}
                      error={errors.dateOfBirth}
                    />
                  </div>
                  <div className="col">
                    <Select
                      name="sex"
                      label="Sex"
                      options={contactOptionSets.gendercode}
                      onChange={handleChange}
                      error={errors.sex}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="ssn"
                      label="SSN"
                      onChange={handleChange}
                      error={errors.ssn}
                    />
                  </div>
                  <div className="col"></div>
                </div>
              </>
            )}
            {(data.client || (data.firstName && data.lastName)) && (
              <>
                <Input
                  name="dateOfReferral"
                  label="Date of Referral"
                  type="date"
                  onChange={handleChange}
                  error={errors.dateOfReferral}
                />
                {!agenciesLoading ? (
                  <>
                    <Input
                      name="referringAgencySearchQuery"
                      label="Search for Agency"
                      placeholder="Search for Agency..."
                      onChange={(e) => {
                        setReferringAgencySearchQuery(e.currentTarget.value);
                      }}
                    />
                    <Select
                      name="referringAgency"
                      label="Referring Agency"
                      options={getReferringAgencyOptions()}
                      value={data.referringAgency}
                      onChange={handleChange}
                      error={errors.referringAgency}
                      disabled={referringAgencyOptions.length === 0}
                    />
                  </>
                ) : (
                  <>Referring Agencies loading...</>
                )}
                <div className="row">
                  <div className="col">
                    <Input
                      name="workerSearchQuery"
                      label="Search for Referring Worker"
                      placeholder="Search for Referring Worker..."
                      onChange={(e) =>
                        setWorkerSearchQuery(e.currentTarget.value)
                      }
                    />
                  </div>
                  <div className="col-3">
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: "30px" }}
                      onClick={handleWorkerSearch}
                      disabled={workerSearchQuery === ""}
                    >
                      Search
                    </button>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Select
                      name="referringWorker"
                      label="Referring Worker"
                      options={workerOptions}
                      onChange={handleChange}
                      error={errors.referringWorker}
                      disabled={!workerSearchCompleted}
                    />
                  </div>
                </div>
                <Select
                  name="programUnit"
                  label="Program Unit"
                  options={programUnitOptions}
                  value={data.programUnit}
                  onChange={handleChange}
                  error={errors.programUnit}
                  disabled={programUnitOptions.length === 0}
                />
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <>
              <button className="btn btn-danger" onClick={handleHideModal}>
                Cancel
              </button>
              <button
                className={
                  Object.keys(errors).length === 0
                    ? "btn btn-primary"
                    : "btn btn-secondary"
                }
                disabled={Object.keys(errors).length !== 0}
                onClick={() => {
                  if (!validate()) {
                    handleSave();
                    handleHideModal();
                  }
                }}
              >
                Save
              </button>
              <button
                className={
                  Object.keys(errors).length === 0
                    ? "btn btn-primary"
                    : "btn btn-secondary"
                }
                disabled={Object.keys(errors).length !== 0}
                onClick={() => {
                  if (!validate()) handleSaveAndAdd();
                }}
              >
                Save and Add New
              </button>
            </>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default ReferralModal;
